import controlsJson from '../data/controls.json';
import provisionsJson from '../data/legal-register.json';
import knowledgeJson from '../data/knowledge-base.json';
import { MasterDataPackage } from '../types/masterData';
import { ControlObligation, StatutoryProvision } from '../types/obligation';
import { ArchitectureSystem } from '../types/system';
import { ComplianceUser } from '../types/user';
import { StatutoryExceptionRecord } from '../types/exception';

export function getDefaultMasterData(): MasterDataPackage {
  const defaultActors = knowledgeJson.actors.map((a, idx) => ({
    id: `ACTOR-${idx + 1}`,
    name: a.role,
    statutory_ref: a.statutory_ref,
    description: a.definition,
    max_penalty: a.max_penalty
  }));

  const defaultDomains = [
    { id: 'DOM-01', slug: 'scope', title: 'Scope & Territorial Perimeter', description: 'Territorial nexus, definitions, and personal data scope.' },
    { id: 'DOM-02', slug: 'purpose', title: 'Grounds & Lawful Purpose', description: 'Consent grounds and section 7 certain legitimate uses.' },
    { id: 'DOM-03', slug: 'notice-consent', title: 'Notice & Consent Architecture', description: 'Itemised notice, multilingual delivery, and consent tokens.' },
    { id: 'DOM-04', slug: 'withdrawal', title: 'Consent Withdrawal & Cessation', description: 'Withdrawal processing and cascading deletion to downstream services.' },
    { id: 'DOM-05', slug: 'consent-managers', title: 'Consent Managers & Interoperability', description: 'Registration, interoperable protocols, and audit logs.' },
    { id: 'DOM-06', slug: 'rights', title: 'Data Principal Rights & Grievance', description: 'Access, correction, erasure, nomination, and grievance timelines.' },
    { id: 'DOM-07', slug: 'children', title: 'Children Data & Parental Consent', description: 'Verifiable parental consent and tracking prohibitions.' },
    { id: 'DOM-08', slug: 'retention', title: 'Retention Limits & Erasure', description: 'Retention lifecycles, legal holds, and automated purge.' },
    { id: 'DOM-09', slug: 'security', title: 'Security Safeguards & Access', description: 'Reasonable technical and organizational security safeguards.' },
    { id: 'DOM-10', slug: 'breach', title: 'Breach Detection & Notification', description: '72-hour Board intimation and affected principal notices.' },
    { id: 'DOM-11', slug: 'processors', title: 'Processors & Third-Party Risk', description: 'Processor agreements, sub-processor rules, and TPRM audits.' },
    { id: 'DOM-12', slug: 'transfers', title: 'Cross-Border Transfers', description: 'International data transfers and negative list restrictions.' },
    { id: 'DOM-13', slug: 'sdf', title: 'Significant Data Fiduciary (SDF)', description: 'Resident DPO, Independent Auditor, DPIA, and Board reports.' },
    { id: 'DOM-14', slug: 'exemptions', title: 'Statutory Exemptions', description: 'Section 17 statutory safe harbours and relief.' },
    { id: 'DOM-15', slug: 'enforcement', title: 'Board Oversight & Enforcement', description: 'Inquiry procedures, summons, and financial penalties.' }
  ];

  const defaultTranches = [
    {
      id: '2025-11-13',
      name: 'Tranche 1',
      date: '13 Nov 2025',
      status: 'In Force',
      headline: 'Statutory Foundation & DPB',
      description: 'Establishment of Data Protection Board of India, inquiry powers, definitions, digital office framework.',
      colorClass: 'emerald'
    },
    {
      id: '2026-11-13',
      name: 'Tranche 2',
      date: '13 Nov 2026',
      status: 'Consent Managers',
      headline: 'Consent Architecture & Interoperability',
      description: 'Registration of Consent Managers under s.6(9), interoperability standards, technical onboarding.',
      colorClass: 'sky'
    },
    {
      id: '2027-05-13',
      name: 'Tranche 3',
      date: '13 May 2027',
      status: 'Core Enterprise Duties',
      headline: 'Enterprise Compliance & Penalties Live',
      description: 'All substantive duties: Notice & Consent, Principal Rights, Retention/Erasure, Breach Notification, SDF Obligations & Penalties up to ₹250 Cr.',
      colorClass: 'amber'
    }
  ];

  const defaultOwners = [
    'General Counsel / Legal',
    'CISO / Security Operations',
    'Data Protection Officer (DPO)',
    'Product Engineering & Architecture',
    'Procurement & Vendor Risk (TPRM)',
    'HR & Employee Data Operations',
    'Marketing & Growth Operations',
    'IT Operations & Infrastructure'
  ];

  const defaultUsers: ComplianceUser[] = [
    {
      id: 'USR-001',
      first_name: 'Priya',
      last_name: 'Sharma',
      designation: 'Data Protection Officer (DPO)',
      email: 'priya.sharma@acmecorp.in',
      phone: '+91 98101 23456',
      department: 'Legal & Compliance',
      mapped_roles: ['Data Protection Officer (DPO)', 'General Counsel / Legal'],
      notes: 'Designated resident Data Protection Officer under DPDP Act s.10(2)(a). Responsible for DPIA and DPB liaison.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'USR-002',
      first_name: 'Rajesh',
      last_name: 'Kumar',
      designation: 'Chief Information Security Officer (CISO)',
      email: 'rajesh.kumar@acmecorp.in',
      phone: '+91 98202 34567',
      department: 'Information Security',
      mapped_roles: ['CISO / Security Operations'],
      notes: 'Leads SOC, CERT-In/DPB 72-hour breach reporting protocols, encryption posture, and access governance.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'USR-003',
      first_name: 'Vikram',
      last_name: 'Mehta',
      designation: 'Lead Privacy Counsel & Grievance Officer',
      email: 'vikram.mehta@acmecorp.in',
      phone: '+91 98303 45678',
      department: 'Legal & Regulatory',
      mapped_roles: ['General Counsel / Legal', 'Data Protection Officer (DPO)'],
      notes: 'Oversees statutory principal grievance redressal SLA within 7 days and contractual processor clauses.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'USR-004',
      first_name: 'Ananya',
      last_name: 'Iyer',
      designation: 'VP, Platform Engineering & Data Architecture',
      email: 'ananya.iyer@acmecorp.in',
      phone: '+91 98404 56789',
      department: 'Platform Engineering',
      mapped_roles: ['Product Engineering & Architecture', 'IT Operations & Infrastructure'],
      notes: 'Architects Consent Token verification in API Gateway, automated erasure pipelines, and tokenization.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'USR-005',
      first_name: 'Rohan',
      last_name: 'Gupta',
      designation: 'Director of IT & Cloud Infrastructure',
      email: 'rohan.gupta@acmecorp.in',
      phone: '+91 98505 67890',
      department: 'IT Operations',
      mapped_roles: ['IT Operations & Infrastructure'],
      notes: 'Manages multi-region cloud residency, automated backups, and log immutability.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'USR-006',
      first_name: 'Sunita',
      last_name: 'Patel',
      designation: 'Head of Vendor Risk & TPRM',
      email: 'sunita.patel@acmecorp.in',
      phone: '+91 98606 78901',
      department: 'Procurement & TPRM',
      mapped_roles: ['Procurement & Vendor Risk (TPRM)'],
      notes: 'Manages Data Processing Agreements (DPAs), sub-processor approvals, and annual third-party audits.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'USR-007',
      first_name: 'Deepa',
      last_name: 'Krishnan',
      designation: 'HR Operations & Employee Privacy Lead',
      email: 'deepa.krishnan@acmecorp.in',
      phone: '+91 98707 89012',
      department: 'Human Resources',
      mapped_roles: ['HR & Employee Data Operations'],
      notes: 'Oversees employee data collection notices, employment legitimate use documentation under s.7(i).',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'USR-008',
      first_name: 'Amitav',
      last_name: 'Sen',
      designation: 'Growth Marketing & MarTech Lead',
      email: 'amitav.sen@acmecorp.in',
      phone: '+91 98808 90123',
      department: 'Marketing Operations',
      mapped_roles: ['Marketing & Growth Operations'],
      notes: 'Ensures marketing consent collection, pixel tracker compliance, and opt-out synchronization.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  ];

  const defaultExceptions: StatutoryExceptionRecord[] = [
    {
      id: 'EXC-001',
      title: 'Section 17(1)(a) Legal Rights Enforcement & Litigation Defense',
      statutory_ground: 'Section 17(1)(a)',
      category: 'legal_claims',
      status: 'ACTIVE',
      justification: 'Processing personal data of litigants, claimants, witnesses, and contractual counterparts necessary for enforcing legal rights and defending claims before judicial courts, arbitral tribunals, and regulatory authorities.',
      mapped_obligation_ids: ['OBL-08', 'OBL-09', 'OBL-19', 'OBL-20', 'OBL-22', 'ACT-5', 'ACT-6', 'ACT-12', 'RULE-3', 'RULE-7'],
      approved_by: 'Vikram Mehta (Lead Privacy Counsel)',
      approval_date: '2025-11-13',
      notes: 'Exempts from Chapter II notice, consent, and erasure obligations. Section 8(5) reasonable security safeguards remain mandatory.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'EXC-002',
      title: 'Section 17(1)(b) Prevention & Investigation of Fraud/Offences',
      statutory_ground: 'Section 17(1)(b)',
      category: 'offence_investigation',
      status: 'ACTIVE',
      justification: 'Processing security telemetry, transaction anomalies, fraud indicators, and audit trails strictly necessary for the prevention, detection, investigation, or prosecution of cyber security incidents and criminal offences.',
      mapped_obligation_ids: ['OBL-08', 'OBL-10', 'OBL-15', 'OBL-21', 'ACT-6', 'RULE-3', 'RULE-6'],
      approved_by: 'Rajesh Kumar (CISO)',
      approval_date: '2025-11-13',
      notes: 'Provides statutory relief from consent requirements for security operations center (SOC) telemetry.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'EXC-003',
      title: 'Section 17(2)(b) Research, Analytics & Statistical Archiving',
      statutory_ground: 'Section 17(2)(b)',
      category: 'research_archival',
      status: 'ACTIVE',
      justification: 'Processing aggregated personal data for historical, statistical, and algorithm research where data is not used to make any decision specific to the data principal.',
      mapped_obligation_ids: ['OBL-08', 'OBL-09', 'OBL-26', 'ACT-5', 'ACT-6', 'RULE-3'],
      approved_by: 'Priya Sharma (Data Protection Officer)',
      approval_date: '2026-01-15',
      notes: 'Conditioned on non-decisioning and adherence to statistical privacy standards.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 'EXC-004',
      title: 'Section 3(c) Foreign Outsourcing & Non-Resident Data Processing',
      statutory_ground: 'Section 3(c)',
      category: 'foreign_outsourcing',
      status: 'ACTIVE',
      justification: 'Processing personal data of data principals not within the territory of India pursuant to cross-border service contracts entered into with foreign clients and fiduciaries.',
      mapped_obligation_ids: ['OBL-08', 'OBL-09', 'OBL-21', 'OBL-22', 'OBL-23', 'OBL-24', 'OBL-42', 'ACT-5', 'ACT-6', 'ACT-9', 'ACT-16', 'RULE-3', 'RULE-10', 'RULE-15'],
      approved_by: 'Vikram Mehta (Lead Privacy Counsel)',
      approval_date: '2026-02-01',
      notes: 'Provides complete relief from Indian notice, consent, and rights chapters for pure offshore outsourcing business lines.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  ];

  const defaultSystems: ArchitectureSystem[] = knowledgeJson.systems.map((s) => ({
    id: s.id,
    name: s.name,
    type: 'Microservice / Backend API',
    description: s.purpose,
    owner: 'Platform Engineering',
    data_categories: ['Digital Personal Data'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }));

  return {
    actors: defaultActors,
    domains: defaultDomains,
    tranches: defaultTranches,
    owners: defaultOwners,
    users: defaultUsers,
    exceptions: defaultExceptions,
    controls: controlsJson as unknown as ControlObligation[],
    provisions: provisionsJson as unknown as StatutoryProvision[],
    systems: defaultSystems,
    updated_at: new Date().toISOString()
  };
}

export const DEFAULT_MASTER_DATA_PACKAGE: MasterDataPackage = getDefaultMasterData();

